import { isNonNil } from './object'
import { convertFromImperial, nameForMetricUnit } from './unitConversions'

/**
 * A factory for number-to-string formatters that, given a number or null/undefined value,
 * returns a string formatted according to the conventions for showing metrics to users in Panorama,
 * given the specified locale (defaults to 'en-US')
 *
 * @example
 * const formatFrench = toStringFormatterFromLocale('fr-FR')
 * const formattedNumber = formatFrench(11.12687) // -> '11,1'
 * const formattedLargeNumber = formatFrench(19812.12343) // -> '19 812'
 * const formattedUndefinedNumber = formatFrench(undefined) // -> '--'
 */
export const toStringFormatterFromLocale = (locale = 'en-US') => {
  // TODO: once 'negative' value is allowed in node and all browsers, remove try/catch
  try {
    const largeIntFormatter = Intl.NumberFormat(locale, {
      style: 'decimal',
      minimumSignificantDigits: 3,
      maximumSignificantDigits: 3,
      compactDisplay: 'short',
      notation: 'compact'
    })
    const intFormatter = Intl.NumberFormat(locale, {
      style: 'decimal',
      maximumFractionDigits: 0,
      signDisplay: 'negative'
    })
    const oneDecimalFormatter = Intl.NumberFormat(locale, {
      style: 'decimal',
      maximumFractionDigits: 1,
      signDisplay: 'negative'
    })
    const twoDecimalFormatter = Intl.NumberFormat(locale, {
      style: 'decimal',
      maximumFractionDigits: 2,
      signDisplay: 'negative'
    })
    const threeDecimalFormatter = Intl.NumberFormat(locale, {
      style: 'decimal',
      maximumFractionDigits: 3,
      signDisplay: 'negative'
    })

    return (num: number): string => {
      const absoluteValue = Math.abs(num)
      if (absoluteValue >= 10_000) return largeIntFormatter.format(num)
      if (absoluteValue >= 1000 || Number.isInteger(num)) return intFormatter.format(num)
      if (absoluteValue >= 10) return oneDecimalFormatter.format(num)
      if (absoluteValue >= 1) return twoDecimalFormatter.format(num)
      return threeDecimalFormatter.format(num)
    }
  } catch {
    const largeIntFormatter = Intl.NumberFormat(locale, {
      style: 'decimal',
      minimumSignificantDigits: 3,
      maximumSignificantDigits: 3,
      compactDisplay: 'short',
      notation: 'compact'
    })
    const intFormatter = Intl.NumberFormat(locale, {
      style: 'decimal',
      maximumFractionDigits: 0
    })
    const oneDecimalFormatter = Intl.NumberFormat(locale, {
      style: 'decimal',
      maximumFractionDigits: 1
    })
    const twoDecimalFormatter = Intl.NumberFormat(locale, {
      style: 'decimal',
      maximumFractionDigits: 2
    })
    const threeDecimalFormatter = Intl.NumberFormat(locale, {
      style: 'decimal',
      maximumFractionDigits: 3
    })

    return (num: number): string => {
      const absoluteValue = Math.abs(num)
      if (absoluteValue >= 10_000) return largeIntFormatter.format(num)
      if (absoluteValue >= 1000 || Number.isInteger(num)) return intFormatter.format(num)
      if (absoluteValue >= 10) return oneDecimalFormatter.format(num)
      if (absoluteValue >= 1) return twoDecimalFormatter.format(num)
      return threeDecimalFormatter.format(num)
    }
  }
}

export type Metric =
  | {
      value: number | string | null | undefined | boolean
      unit?: string
    }
  | boolean
  | null
  | undefined
  | number
  | string

type FormatOptions = {
  isMetricSystem?: boolean
  locale?: string
}

/**
 * We will format bulk units as integers when the value is large enough,
 * so that reports are as meaningful as possible.
 *
 * The undefined is to ensure typescript will let us check for inclusion of unit in the array
 */
const BULK_UNITS: (string | undefined)[] = ['ac', 'bu', 'lbs', 'gal']

export const toMetricsFormatter = ({
  isMetricSystem = false,
  locale = 'en-US'
}: FormatOptions = {}) => {
  const formatNumber = toStringFormatterFromLocale(locale)
  const intFormatter = Intl.NumberFormat(locale, {
    style: 'decimal',
    maximumFractionDigits: 0
  })
  const formatInt = (value: number) => intFormatter.format(value)

  const format = (value: Metric, opts: { unit?: string } = {}): [string, string | null] => {
    const noValue = '--'

    switch (typeof value) {
      case 'string':
        return value.length > 0 ? [value, null] : [noValue, null]

      case 'number': {
        if (Number.isNaN(value)) return [noValue, null]

        const num =
          isMetricSystem && isNonNil(opts.unit) ? convertFromImperial(value, opts.unit) : value

        const format = num > 1000 && BULK_UNITS.includes(opts.unit) ? formatInt : formatNumber
        const unit =
          isMetricSystem && isNonNil(opts.unit) ? nameForMetricUnit(opts.unit) : opts.unit || null
        return [format(num), unit]
      }

      case 'boolean':
        return [value ? 'True' : 'False', null]

      case 'undefined':
        return [noValue, null]

      case 'object':
        if (value === null) return [noValue, null]

        return format(value.value, {
          unit: value.unit
        })

      default:
        console.error('Unknown value type', value)
        return [noValue, null]
    }
  }

  return format
}
